import React from 'react';
import { Activity, Boxes, BellRing, AlertCircle, AlertTriangle, Info, Package, Users, Store, ArrowLeftRight, ClipboardList, Receipt, ShoppingBag, PackageCheck, FileText } from 'lucide-react';
import { Card, CardHeader, Button, EmptyState, fmtInr, fmtNum, timeAgo } from '../ui';

const KIND_ICON = { stock: ArrowLeftRight, sku: Package, customer: Users, vendor: Store, so: ClipboardList, invoice: Receipt, po: ShoppingBag, grn: PackageCheck, quote: FileText };

export const ActivityFeed = ({ items = [], go }) => (
  <Card className="p-0 erp-rise erp-d3" data-testid="activity-card">
    <CardHeader icon={Activity} title="Recent Activities" action={<Button variant="soft" size="sm" onClick={() => go('inventory/ledger')} data-testid="activity-view-all">View All</Button>} />
    {items.length === 0 ? <EmptyState icon={Activity} title="No activity yet" text="Create SKUs, customers or post stock movements to see them here." /> : (
      <ul className="px-3 pb-3">
        {items.map((a, i) => {
          const Icon = KIND_ICON[a.kind] || Activity;
          return (
            <li key={`${a.at}-${i}`} onClick={a.view ? () => go(a.view) : undefined} className={`flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[#f8faff] transition-colors ${a.view ? 'cursor-pointer' : ''}`}>
              <span className="h-8 w-8 rounded-lg grid place-items-center flex-shrink-0" style={{ background: 'var(--erp-blue-soft)', color: 'var(--erp-blue)' }}><Icon size={15} /></span>
              <span className="flex-1 text-sm font-medium truncate" style={{ color: '#2b365a' }}>{a.text}</span>
              <span className="text-xs whitespace-nowrap" style={{ color: 'var(--erp-muted)' }}>{timeAgo(a.at)}</span>
            </li>
          );
        })}
      </ul>
    )}
  </Card>
);

export const StockPosition = ({ rows = [], go }) => (
  <Card className="p-0 erp-rise erp-d4" data-testid="stock-position-card">
    <CardHeader icon={Boxes} title="Stock Position" subtitle="Live · by business vertical" action={<Button variant="soft" size="sm" onClick={() => go('inventory')} data-testid="stock-view-all">View All</Button>} />
    {rows.length === 0 ? <EmptyState icon={Boxes} title="No stock yet" text="Post a purchase receipt under Inventory to see live valuation." /> : (
      <table className="w-full text-sm mb-2">
        <thead><tr className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--erp-muted)' }}>
          <th className="text-left font-semibold px-5 py-1.5">Category</th><th className="text-right font-semibold py-1.5">Items</th><th className="text-right font-semibold py-1.5">Qty</th><th className="text-right font-semibold px-5 py-1.5">Value</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.category} className="border-t" style={{ borderColor: '#f0f3f9' }}>
              <td className="px-5 py-2.5 font-semibold">{r.category}</td>
              <td className="py-2.5 text-right">{fmtNum(r.items)}</td>
              <td className="py-2.5 text-right">{fmtNum(r.qty)}</td>
              <td className="px-5 py-2.5 text-right font-bold">{fmtInr(r.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
);

const LEVEL = {
  danger: { icon: AlertCircle, bg: '#fdecec', fg: '#c0262d' },
  warn: { icon: AlertTriangle, bg: '#fff4e0', fg: '#d97706' },
  info: { icon: Info, bg: 'var(--erp-blue-soft)', fg: 'var(--erp-blue)' },
};

export const AlertsPanel = ({ alerts = [], go }) => (
  <Card className="p-0 erp-rise erp-d5" data-testid="alerts-card">
    <CardHeader icon={BellRing} title="Alerts & Approvals" action={<Button variant="soft" size="sm" onClick={() => go('inventory')} data-testid="alerts-view-all">View All</Button>} />
    {alerts.length === 0 ? <EmptyState icon={BellRing} title="All clear" text="No pending alerts. Nice work." /> : (
      <ul className="px-3 pb-3">
        {alerts.map((a) => {
          const L = LEVEL[a.level] || LEVEL.info; const Icon = L.icon;
          return (
            <li key={a.text}>
              <button type="button" onClick={() => go(a.view)} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[#f8faff] transition-colors text-left" data-testid={`alert-${a.view}`}>
                <span className="h-7 w-7 rounded-full grid place-items-center flex-shrink-0" style={{ background: L.bg, color: L.fg }}><Icon size={14} /></span>
                <span className="text-sm font-extrabold w-6" style={{ color: L.fg }}>{a.count}</span>
                <span className="flex-1 text-sm font-medium" style={{ color: '#2b365a' }}>{a.text}</span>
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </Card>
);
